import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { auth, googleProvider, db } from '../../lib/firebase';
import { User } from '../../types/models';
import { ensureDistanceUnit } from '../../lib/profile';
import { trackError, ERROR_CODES, createErrorMeta } from '../../lib/telemetry';
// import { openModal } from '../slices/uiSlice'; // For future onboarding integration

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  initialized: boolean;
}

const initialState: AuthState = {
  user: null,
  isLoading: false,
  isAuthenticated: false,
  error: null,
  initialized: false,
};

// Helper function to get detailed error message
const getDetailedErrorMessage = (error: any): string => {
  console.error('Firebase Auth Error:', error);
  console.error('Error details:', {
    code: error.code,
    message: error.message,
    stack: error.stack,
    customData: error.customData,
  });

  if (error.code) {
    switch (error.code) {
      case 'auth/unauthorized-domain':
        return 'This domain is not authorized for authentication. Please contact support.';
      case 'auth/popup-blocked':
        return 'Popup was blocked by your browser. Please allow popups and try again.';
      case 'auth/popup-closed-by-user':
        return 'Sign-in was cancelled. Please try again.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your internet connection and try again.';
      case 'auth/internal-error':
        return 'Internal authentication error. This may be due to App Check configuration or domain mismatch. Please try again or contact support.';
      case 'auth/app-check-token-expired':
        return 'App Check token expired. Please refresh the page and try again.';
      case 'auth/app-check-token-invalid':
        return 'App Check validation failed. Please refresh the page and try again.';
      case 'permission-denied':
      case 'firestore/permission-denied':
        return 'Permission denied. Your account may not have the necessary permissions. Please contact support.';
      case 'firestore/unavailable':
        return 'Database temporarily unavailable. Please try again in a moment.';
      default:
        return `Authentication error (${error.code}): ${error.message}`;
    }
  }

  if (error.message?.includes('Missing or insufficient permissions')) {
    return 'Database permission error. Your account may not be properly configured. Please contact support.';
  }

  return error.message || 'An unexpected error occurred during sign-in.';
};

// Async thunks
export const signInWithGoogle = createAsyncThunk(
  'auth/signInWithGoogle',
  async (_, { rejectWithValue }) => {
    try {
      console.log('Starting Google sign-in...');
      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;
      console.log('Google sign-in successful, user:', firebaseUser.uid);

      // Check if user document exists
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      console.log('Checking user document at path:', `users/${firebaseUser.uid}`);

      const userDoc = await getDoc(userDocRef);
      console.log('User document exists:', userDoc.exists());

      let userData: User;

      if (userDoc.exists()) {
        userData = userDoc.data() as User;
        console.log('Loaded existing user data');
      } else {
        // Create new user document
        console.log('Creating new user document...');
        userData = {
          uid: firebaseUser.uid,
          displayName: firebaseUser.displayName || '',
          email: firebaseUser.email || '',
          photoURL: firebaseUser.photoURL || undefined,
          province: '', // NO DEFAULT - User must select their province (critical for tax rates)
          distanceUnit: 'km', // Default to km
          gstRegistered: false,
          preferredTaxPeriod: {
            startDate: new Date(new Date().getFullYear(), 0, 1), // Jan 1
            endDate: new Date(new Date().getFullYear(), 11, 31), // Dec 31
          },
          createdAt: new Date() as any,
          updatedAt: new Date() as any,
        };

        await setDoc(userDocRef, userData);
        console.log('New user document created successfully');
      }

      return userData;
    } catch (error: any) {
      const detailedError = getDetailedErrorMessage(error);
      console.error('Sign-in error:', detailedError);

      // Track authentication error
      const errorMeta = createErrorMeta(
        'auth',
        ERROR_CODES.AUTH_TOKEN_INVALID,
        error.code?.includes('auth/') ? 'S2' : 'S3',
        'user-blocking',
        '24h',
        {
          retryable: true,
          providerCode: error.code,
          userAction: 'google_signin',
          extras: { errorDetails: detailedError },
        }
      );
      trackError('Google sign-in failed', errorMeta, error);

      return rejectWithValue(detailedError);
    }
  }
);

export const signOut = createAsyncThunk('auth/signOut', async (_, { rejectWithValue }) => {
  try {
    await firebaseSignOut(auth);
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async (updates: Partial<User>, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      const user = state.auth.user;

      if (!user) {
        throw new Error('No user logged in');
      }

      const userDocRef = doc(db, 'users', user.uid);
      const updatedData = {
        ...updates,
        updatedAt: Timestamp.now(),
      };

      await updateDoc(userDocRef, updatedData);

      return { ...user, ...updatedData };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const initializeAuth = createAsyncThunk('auth/initialize', async (_, _thunkAPI) => {
  return new Promise<User | null>(resolve => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        try {
          console.log('Auth state changed - user found:', firebaseUser.uid);

          // Force token refresh to ensure it's valid
          // DISABLED AUTO-SIGN-OUT - Just log errors for now
          try {
            console.log('Refreshing auth token in initializeAuth...');
            await firebaseUser.getIdToken(true);
            console.log('✅ Auth token refreshed successfully in initializeAuth');
          } catch (tokenError: any) {
            console.error('❌ Token refresh failed in initializeAuth:', tokenError);
            console.error('Token error details:', {
              code: tokenError.code,
              message: tokenError.message,
            });

            // DISABLED: Don't auto-sign-out anymore
            // Just log the error and continue loading user data
            console.warn('⚠️ Token refresh failed in initializeAuth but continuing anyway.');
          }

          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data() as User;
            console.log('User document loaded successfully', {
              uid: firebaseUser.uid,
              hasBilling: !!userData.billing,
              billingPlan: userData.billing?.plan,
              billingStatus: userData.billing?.status,
            });

            // Auto-migrate existing users to have default distance unit
            ensureDistanceUnit(firebaseUser.uid).catch(console.error);

            resolve(userData);
          } else {
            console.log('User document does not exist');
            resolve(null);
          }
        } catch (error: any) {
          console.error('Error fetching user data:', error);

          // DISABLED: Don't auto-sign-out on permissions errors
          // Just log and resolve null - let app handle it normally
          if (
            error.code === 'permission-denied' ||
            error.message?.includes('Missing or insufficient permissions')
          ) {
            console.warn('⚠️ Permissions error detected but NOT forcing sign-out. Continuing.');
          }

          resolve(null);
        }
      } else {
        console.log('Auth state changed - no user');
        resolve(null);
      }
      unsubscribe();
    });
  });
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // Initialize auth
      .addCase(initializeAuth.pending, state => {
        state.isLoading = true;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.initialized = true;
        state.user = action.payload;
        state.isAuthenticated = !!action.payload;
      })
      .addCase(initializeAuth.rejected, state => {
        state.isLoading = false;
        state.initialized = true;
        state.user = null;
        state.isAuthenticated = false;
      })

      // Sign in with Google
      .addCase(signInWithGoogle.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signInWithGoogle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.initialized = true; // Ensure initialized is set after successful sign-in
        state.error = null;
      })
      .addCase(signInWithGoogle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      })

      // Sign out
      .addCase(signOut.pending, state => {
        state.isLoading = true;
      })
      .addCase(signOut.fulfilled, state => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(signOut.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update user profile
      .addCase(updateUserProfile.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setUser } = authSlice.actions;
export default authSlice.reducer;
